import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { Context as SvgContext } from "svgcanvas"

// Geometria etykiety wartości (chip pod liczbą) - w jednym miejscu zamiast magicznych liczb w rysowaniu.
const LABEL = {
  font: "600 11px ui-sans-serif, system-ui, sans-serif",
  padX: 3,
  padY: 1.5,
  h: 12,
  offsetAbove: 10, // etykieta dodatniej wartości nad punktem
  offsetBelow: 12, // etykieta ujemnej wartości pod punktem
}

// Renderuje wykres raportu Insight z danych przekazanych w atrybutach data-* canvasu.
// Tytuł, parametry i opis obserwacji są wbudowane w canvas (title + subtitle), żeby zrzut
// ekranu zachowywał komplet informacji. Obsługuje limit serii (top-N), serię zbiorczą "Wszyscy",
// typ wykresu (linia / słupki) i etykiety wartości - wszystko przełączane na żywo.
//
// AKTUALIZACJA WYKRESU idzie ZAWSZE przez applyChange(level) - nie wołaj render/update ręcznie:
//   "rebuild" - zmiana TYPU wykresu (Chart.js nie zmienia typu w locie -> niszczymy i tworzymy od nowa; reset izolacji)
//   "reshape" - zmiana ZESTAWU/kolejności serii (limit, kierunek, switch "Wszyscy" -> przebudowa z fullDatasets; reset izolacji)
//   "repaint" - sam WYGLĄD bez zmiany danych (pokaż wartości, izolacja serii -> tylko przerysowanie)
// Nowy feature wybiera poziom, dzięki czemu nie da się pomylić toru aktualizacji.
// Connects to data-controller="report-chart"
export default class extends Controller {
  static targets = ["canvas", "limit", "directionToggle", "directionInput", "overallInput", "seriesInclude", "seriesExclude", "legend"]

  connect() {
    this.direction = "top" // kierunek limitu: "top" = najwięcej, "bottom" = najmniej (po sumie)
    this.seriesInclude = "" // pole "pokaż serie" - tylko pasujące (albo wszystkie gdy puste)
    this.seriesExclude = "" // pole "ukryj serie" - pasujące chowamy
    if (!this.hasCanvasTarget) return // brak wykresu (pusty stan) - nie ma czego renderować

    // odtwórz kierunek z ukrytego pola - przetrwał submit "Generuj"
    if (this.hasDirectionInputTarget && this.directionInputTarget.value) this.direction = this.directionInputTarget.value

    const canvas = this.canvasTarget
    const reportData = JSON.parse(canvas.dataset.report)
    this.title = canvas.dataset.title
    this.subtitle = JSON.parse(canvas.dataset.subtitle)
    this.prioritySeries = canvas.dataset.prioritySeries // linia zbiorcza (np. "Nieprzypisane") - na początek
    this.overallSeries = canvas.dataset.overallSeries // seria "Wszyscy" - poza limitem, sterowana switchem
    // stan switcha "Wszyscy" odtworzony z ukrytego pola (przetrwał Generuj); domyślnie same osoby
    this.overallState = (this.hasOverallInputTarget && this.overallInputTarget.value) || "people"
    this.chartType = canvas.dataset.chartType || "line" // line | grouped | stacked
    // "pokaż wartości" (na żywo): wartości przy WSZYSTKICH punktach; stan z pola (przetrwał Generuj)
    this.showAllValues = canvas.dataset.showValues === "true"

    // UWAGA na nazewnictwo: serie (encje) vs oś X (daty) - łatwo pomylić
    const seriesLabels = reportData.keys          // nazwy serii: statusy / osoby / kategorie...
    this.dateKeys = Object.keys(reportData.data)  // klucze osi X: daty / okresy
    const valuesByDate = Object.values(reportData.data) // per data: tablica wartości w kolejności serii

    // spójna paleta (Tailwind) zamiast surowych rgb - czytelne, odróżnialne kolory
    const palette = [
      "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
      "#8b5cf6", "#06b6d4", "#ec4899", "#64748b",
    ]

    this.focused = null // indeks wyizolowanej serii (klik); null = brak izolacji

    // pełny zestaw serii (bez limitu i bez stylu typu) - baza, z której przeliczamy widok (shapeDatasets)
    this.fullDatasets = seriesLabels.map((label, index) => {
      const color = palette[index % palette.length]
      const data = {}
      this.dateKeys.forEach((key, day) => {
        // wartości z BigDecimal (np. percentile_cont) przychodzą jako stringi - Chart.js wymaga liczb
        const v = valuesByDate[day][index]
        data[key] = v == null ? null : Number(v)
      })
      return {
        label,
        data,
        _color: color, // oryginalny kolor - do przywrócenia po wyjściu z izolacji
        borderColor: color,
        backgroundColor: color,
      }
    })

    // odtwórz filtry serii z pól (przetrwały "Generuj"/reload przez paramy)
    if (this.hasSeriesIncludeTarget) this.seriesInclude = this.seriesIncludeTarget.value
    if (this.hasSeriesExcludeTarget) this.seriesExclude = this.seriesExcludeTarget.value

    this.render()
    this.syncDirectionToggle() // ustaw strzałkę zgodnie z odtworzonym kierunkiem
  }

  // === JEDYNE wejście do aktualizacji wykresu (patrz komentarz klasy: rebuild/reshape/repaint) ===
  applyChange(level) {
    if (level === "rebuild") { // zmiana typu - pełna przebudowa
      this.focused = null
      this.focusedLabel = null
      this.render()
      return
    }
    if (!this.chart) return

    if (level === "reshape") { // zmiana zestawu serii - odbuduj z fullDatasets (izolacja znika)
      this.focused = null
      this.focusedLabel = null
      this.chart.data.datasets = this.shapeDatasets()
    }
    this.applyFilter() // re-aplikuje ręczny filtr serii i domyka update (repaint/reshape)
  }

  // pola "pokaż serie" / "ukryj serie" pod wykresem - odczyt obu i przefiltrowanie na żywo
  filterSeries() {
    if (this.hasSeriesIncludeTarget) this.seriesInclude = this.seriesIncludeTarget.value
    if (this.hasSeriesExcludeTarget) this.seriesExclude = this.seriesExcludeTarget.value
    this.applyFilter()
  }

  // widoczna seria = pasuje do "pokaż" (albo pole puste) ORAZ nie pasuje do "ukryj".
  // W każdym polu po przecinku = kilka fraz, dopasowanie po fragmencie (bez wielkości liter). Na żywo.
  applyFilter() {
    if (!this.chart) return

    const datasets = this.chart.data?.datasets
    if (datasets && this.chart.setDatasetVisibility) {
      const parse = (s) => (s || "").split(",").map(t => t.trim().toLowerCase()).filter(Boolean)
      const include = parse(this.seriesInclude)
      const exclude = parse(this.seriesExclude)
      datasets.forEach((d, i) => {
        const label = (d.label || "").toLowerCase()
        const included = include.length === 0 || include.some(t => label.includes(t))
        const excluded = exclude.length > 0 && exclude.some(t => label.includes(t))
        this.chart.setDatasetVisibility(i, included && !excluded)
      })
    }
    this.chart.update()
    this.renderLegend()
  }

  // Pobiera wykres jako PNG w wyższej rozdzielczości (3x - ostry, nie rozmyty). Nasza legenda jest w HTML
  // (poza canvasem), więc na czas eksportu włączamy wbudowaną legendę Chart.js i sklejamy na białym tle.
  downloadPng() {
    if (!this.chart) return

    const legend = this.chart.options.plugins.legend
    const prevDisplay = legend.display
    const prevDpr = this.chart.options.devicePixelRatio

    legend.display = true
    this.chart.options.devicePixelRatio = 3 // renderuj gęściej -> ostry eksport niezależnie od ekranu
    this.chart.resize()
    this.chart.update("none") // synchronicznie, bez animacji

    const src = this.chart.canvas
    const out = document.createElement("canvas")
    out.width = src.width
    out.height = src.height
    const ctx = out.getContext("2d")
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, out.width, out.height)
    ctx.drawImage(src, 0, 0)
    const url = out.toDataURL("image/png")

    legend.display = prevDisplay // przywróć stan żywego wykresu (bez wbudowanej legendy, oryginalna rozdzielczość)
    this.chart.options.devicePixelRatio = prevDpr
    this.chart.resize()
    this.chart.update("none")

    const link = document.createElement("a")
    link.href = url
    link.download = `${this.title || "wykres"}.png`
    link.click()
  }

  // Pobiera wykres jako SVG (wektor - ostry w każdej skali). Renderuje świeży wykres do kontekstu
  // svgcanvas. Opcje: bez responsywności/animacji/zdarzeń, DPR 1, z wbudowaną legendą.
  // Serie bierze z ŻYWEGO wykresu (nie z shapeDatasets), więc SVG oddaje dokładnie to, co widać:
  // izolację klikiem (focus przygasza pozostałe), filtr serii (ukryte pomijamy) oraz etykiety wartości
  // (ten sam plugin co żywy wykres - svgcanvas ma roundRect/measureText) - tak jak zrzut PNG.
  downloadSvg() {
    if (!this.chart) return

    const width = this.chart.width
    const height = this.chart.height
    const svgCtx = new SvgContext(width, height)
    this.patchSvgCtx(svgCtx) // svgcanvas ma większość metod; dopinamy tylko te nieliczne, których nie ma

    // fake-canvas udający element canvas na tyle, by DomPlatform go przyjął. Chart.js v4 w przeglądarce
    // zawsze bierze DomPlatform (nie BasicPlatform), a jego initCanvas/releaseContext wołają getAttribute
    // (width/height jako string -> pomija pomiar DOM) oraz set/removeAttribute (release przy destroy).
    // getContext zwraca kontekst SVG, svgCtx.canvas === fakeCanvas (acquireContext tego wymaga).
    // Świeży wykres wiąże SVG-kontekst ze wszystkimi elementami/skalami (podmiana ctx w żywym nie działa -
    // skale trzymają własną referencję). draw() rysuje synchronicznie.
    const fakeCanvas = {
      width,
      height,
      style: {},
      getContext: () => svgCtx,
      getAttribute: (name) => (name === "width" ? String(width) : name === "height" ? String(height) : null),
      setAttribute() {},
      removeAttribute() {},
      addEventListener() {},
      removeEventListener() {},
      getBoundingClientRect: () => ({ left: 0, top: 0, right: width, bottom: height, width, height }),
    }
    svgCtx.canvas = fakeCanvas

    const options = this.buildOptions()
    options.responsive = false
    options.animation = false
    options.events = []
    options.devicePixelRatio = 1
    options.plugins.legend.display = true

    // Klonujemy serie z ŻYWEGO wykresu (a nie shapeDatasets), żeby SVG oddał aktualny stan: kolory po
    // izolacji klikiem (focus mutuje borderColor/backgroundColor) i filtr serii (ukryte pomijamy). Dzięki
    // temu do konstruktora idą TYLKO widoczne serie -> skale i legenda są poprawne w jednym draw().
    // Chart.js v4 rysuje linię przez Path2D (ctx.stroke(path)), a svgcanvas ignoruje argument stroke() -
    // linia gubi się w SVG (słupki/kropki idą wprost na ctx, więc są ok). Ustawienie segment:{} wymusza
    // gałąź strokePathDirect (beginPath/lineTo/stroke wprost na ctx) - svgcanvas ją złapie, wygląd bez zmian.
    const datasets = this.chart.data.datasets
      .filter((_d, i) => this.chart.isDatasetVisible(i))
      .map((d) => {
        const copy = { ...d }
        if (this.chartType === "line") copy.segment = {}
        return copy
      })

    const tmp = new Chart(fakeCanvas, {
      type: this.chartType === "line" ? "line" : "bar",
      data: { labels: this.dateKeys, datasets },
      options,
      plugins: [this.valueLabelsPlugin()], // etykiety wartości - jak na żywym wykresie / w PNG
    })
    tmp.draw()
    const svg = svgCtx.getSerializedSvg()
    tmp.destroy()
    if (!svg) return

    const link = document.createElement("a")
    link.href = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }))
    link.download = `${this.title || "wykres"}.svg`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  // Dopina nieliczne metody Canvas API, których svgcanvas nie ma, a Chart.js v4 ich używa.
  // Guardy `if (!ctx.x)` - dopisujemy tylko realnie brakujące (reszta jest natywnie w svgcanvas).
  patchSvgCtx(ctx) {
    if (!ctx.getLineDash) ctx.getLineDash = function () { return this._lineDash || [] }
    if (!ctx.getContextAttributes) ctx.getContextAttributes = function () { return { alpha: true } }
    if (!ctx.resetClip) ctx.resetClip = function () {}
  }

  // Własna legenda HTML (obok pól filtra): kolorowy znacznik + nazwa serii; klik przełącza widoczność.
  // Ukryte serie (przez filtr lub klik) - przygaszone i przekreślone.
  renderLegend() {
    if (!this.hasLegendTarget || !this.chart) return

    const ul = this.legendTarget
    ul.replaceChildren()
    this.chart.data.datasets.forEach((d, i) => {
      const visible = this.chart.isDatasetVisible(i)
      const li = document.createElement("li")
      li.className = `flex items-center gap-1.5 cursor-pointer select-none ${visible ? "" : "opacity-40 line-through"}`
      const swatch = document.createElement("span")
      swatch.className = "inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
      swatch.style.backgroundColor = d._color || d.borderColor
      const text = document.createElement("span")
      text.textContent = d.label
      li.append(swatch, text)
      li.addEventListener("click", () => {
        this.chart.setDatasetVisibility(i, !this.chart.isDatasetVisible(i))
        this.chart.update()
        this.renderLegend()
      })
      ul.appendChild(li)
    })
  }

  // Niskopoziomowe stworzenie wykresu od zera. Wołane z connect (init) i przez applyChange("rebuild").
  // Chart.js nie zmienia typu w locie, więc przebudowa = destroy + new Chart.
  render() {
    if (this.chart) this.chart.destroy()

    const type = this.chartType === "line" ? "line" : "bar"
    this.chart = new Chart(this.canvasTarget, {
      type,
      data: { labels: this.dateKeys, datasets: this.shapeDatasets() },
      options: this.buildOptions(),
      plugins: [this.valueLabelsPlugin()],
    })
    this.applyFilter() // utrzymaj ręczny filtr serii po przebudowie (nowy Chart resetuje widoczność)
  }

  // --- Handlery kontrolek (akcje Stimulus): ustaw stan + wybierz poziom aktualizacji ---

  // switch "Wszyscy" (osobny kontroler tri-state-switch)
  overallChanged() {
    this.overallState = this.overallInputTarget.value
    this.applyChange("reshape")
  }

  // zmiana typu wykresu (linia / słupki); wybór leci też w paramie (przetrwa "Generuj")
  chartTypeChanged(e) {
    this.chartType = e.target.value
    this.applyChange("rebuild")
  }

  // checkbox "pokaż wartości" - tylko przerysowanie (plugin czyta this.showAllValues)
  toggleValues(e) {
    this.showAllValues = e.target.checked
    this.applyChange("repaint")
  }

  // pole limitu top-N (action "refresh" w ERB) - zmiana liczby serii to reshape
  refresh() {
    this.applyChange("reshape")
  }

  // strzałka kierunku limitu (najwięcej <-> najmniej) - zapisz do pola i reshape
  toggleDirection() {
    this.direction = this.direction === "top" ? "bottom" : "top"
    if (this.hasDirectionInputTarget) this.directionInputTarget.value = this.direction // by przetrwało Generuj
    this.syncDirectionToggle()
    this.applyChange("reshape")
  }

  // klik w serię: wyizoluj klikniętą, resztę przygaś; ponowny klik w tę samą = reset.
  // Mutuje kolory ŻYWYCH datasetów - stan ulotny: znika przy najbliższym reshape (odbudowa z fullDatasets).
  focusSeries(idx) {
    const reset = this.focused === idx
    this.chart.data.datasets.forEach((d, i) => {
      const color = (reset || i === idx) ? d._color : this.fade(d._color, 0.2)
      d.borderColor = color
      d.backgroundColor = color
    })
    this.focused = reset ? null : idx
    // label wyizolowanej serii - plugin etykiet dopasowuje po labelu, nie indeksie (indeksy różnią się
    // między żywym wykresem a eksportem SVG, bo SVG pomija ukryte filtrem serie)
    this.focusedLabel = reset ? null : this.chart.data.datasets[idx].label
    this.applyChange("repaint")
  }

  // ustawia wygląd strzałki (obrót + tooltip) zgodnie z bieżącym kierunkiem
  syncDirectionToggle() {
    if (!this.hasDirectionToggleTarget) return

    const btn = this.directionToggleTarget
    btn.classList.toggle("rotate-180", this.direction === "bottom")
    btn.title = this.direction === "top" ? btn.dataset.labelTop : btn.dataset.labelBottom
  }

  // === Budowa serii i opcji wykresu ===

  // Kształtuje serie wg kontrolek: limit (ile) + kierunek (najwięcej/najmniej po sumie) + styl typu.
  // Ranking ZAWSZE po sumie (łączny wolumen). Na końcu pinuje linię zbiorczą na początek legendy.
  shapeDatasets() {
    const props = this.typeProps()
    // clip: false -> markery/kropki przy krawędziach (np. maks wartość u góry) rysują się w całości,
    // nie są przycinane do obszaru wykresu (jest na to zapas w layout.padding)
    let datasets = this.fullDatasets.map(d => ({ ...d, ...props, clip: false })) // płytka kopia - data tylko czytamy

    // seria zbiorcza "Wszyscy" - wyłączamy ją z limitu top-N; o jej pokazaniu decyduje stan switcha
    let overall = null
    if (this.overallSeries) {
      const oi = datasets.findIndex(d => d.label === this.overallSeries)
      if (oi >= 0) overall = datasets.splice(oi, 1)[0]
    }

    // stan "samo Wszyscy": pomijamy serie osób, zostaje tylko linia zbiorcza (przerywana)
    if (overall && this.overallState === "overall") return [{ ...overall, borderDash: [6, 4] }]

    const limit = this.hasLimitTarget ? parseInt(this.limitTarget.value, 10) : 0
    const direction = this.direction || "top"

    if (limit && datasets.length > limit) {
      const seriesTotal = (d) => Object.values(d.data).reduce((a, b) => a + (Number(b) || 0), 0)
      datasets.sort((a, b) => seriesTotal(b) - seriesTotal(a)) // malejąco po sumie
      // limit ucina resztę: "top" = N z góry (najwięcej), "bottom" = N z dołu (najmniej). Reszty nie pokazujemy.
      datasets = direction === "bottom" ? datasets.slice(-limit) : datasets.slice(0, limit)
    }

    if (this.prioritySeries) {
      const i = datasets.findIndex(d => d.label === this.prioritySeries)
      if (i > 0) datasets.unshift(datasets.splice(i, 1)[0])
    }

    // stan "osoby + Wszyscy": dokładamy linię zbiorczą (przerywaną) na początek legendy
    if (overall && this.overallState === "both") datasets.unshift({ ...overall, borderDash: [6, 4] })
    return datasets
  }

  // Właściwości serii zależne od typu wykresu (linia: gładka krzywa bez punktów; słupki: węższe z zaokrągleniem).
  typeProps() {
    if (this.chartType === "line") {
      return {
        borderWidth: 2,
        cubicInterpolationMode: "monotone", // gładko, ale bez przestrzeliwania poza lokalne wartości
        // kropki tylko na krańcach segmentów (tam gdzie linia urywa się przez przerwę w danych),
        // żeby pojedyncze/zakończone punkty nie znikały; ciągłe wnętrze segmentu - 0 (hover dorysuje)
        pointRadius: (ctx) => {
          const labels = ctx.chart.data.labels
          const data = ctx.dataset.data
          const i = ctx.dataIndex
          if (data[labels[i]] == null) return 0 // brak punktu (przerwa) - nic nie rysujemy

          const prevGap = i === 0 || data[labels[i - 1]] == null
          const nextGap = i === labels.length - 1 || data[labels[i + 1]] == null
          return prevGap || nextGap ? 1.5 : 0 // kraniec segmentu (sąsiad pusty/poza zakresem)
        },
        pointHoverRadius: 5,
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
        fill: false,
      }
    }
    return { borderWidth: 1, borderRadius: 3, categoryPercentage: 0.7, barPercentage: 0.9 }
  }

  // Opcje wykresu. stacked/bars wyprowadzone z this.chartType (jedno źródło prawdy).
  buildOptions() {
    const stacked = this.chartType === "stacked"
    const bars = this.chartType !== "line"
    return {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2.4,
      interaction: { mode: "nearest", intersect: false }, // hover łapie najbliższą serię
      // klik w serię izoluje ją (resztę przygasza); klik w tę samą = reset
      onClick: (event, _elements, chart) => {
        const hit = chart.getElementsAtEventForMode(event, "nearest", { intersect: false }, true)[0]
        if (hit) this.focusSeries(hit.datasetIndex)
      },
      // odstęp górny - etykiety wartości (przy izolacji serii) rysują się nad punktem,
      // bez zapasu nachodziłyby na podtytuł przy górnych punktach
      layout: { padding: { top: 18, right: 12, bottom: 4, left: 4 } },
      scales: {
        x: {
          stacked,
          grid: { display: false },
          ticks: { color: "#6b7280", font: { size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
        },
        y: {
          stacked,
          beginAtZero: true,
          grid: {
            // przy słupkach (delty +/-) linia zera to punkt odniesienia - ciemniejsza niż reszta siatki
            // (Chart.js bywa woła tę funkcję bez ctx.tick - stąd optional chaining)
            color: (ctx) => (bars && ctx.tick?.value === 0 ? "#9ca3af" : "#f1f5f9"),
          },
          border: { display: false },
          ticks: { color: "#6b7280", font: { size: 11 }, precision: 0 },
        },
      },
      plugins: {
        title: {
          display: true,
          text: this.title,
          font: { size: 16, weight: "bold" },
          color: "#374151", // gray-700 - tytuł nieco łagodniejszy niż czerń
          padding: { top: 6, bottom: 2 },
        },
        subtitle: {
          display: true,
          text: this.subtitle,
          color: "#6b7280",
          font: { size: 11 },
          padding: { bottom: 22 }, // nieco większy odstęp podtytułu od obszaru wykresu
        },
        legend: {
          // własna legenda HTML obok pól filtra (renderLegend); wbudowaną wyłączamy tylko gdy mamy target
          display: !this.hasLegendTarget,
          position: "bottom",
          labels: { boxWidth: 12, boxHeight: 12, padding: 16, color: "#374151", font: { size: 12 } },
        },
        tooltip: {
          mode: "nearest",
          intersect: false,
          backgroundColor: "#111827",
          padding: 10,
          cornerRadius: 6,
          titleColor: "#ffffff",
          bodyColor: "#e5e7eb",
          boxPadding: 4,
        },
      },
    }
  }

  // === Etykiety wartości (plugin Chart.js) ===

  // Plugin: wypisuje wartości przy punktach/słupkach. Domyślnie tylko dla wyizolowanej serii (klik),
  // a przy włączonym "pokaż wartości" (this.showAllValues) - dla wszystkich widocznych serii.
  valueLabelsPlugin() {
    return {
      id: "valueLabels",
      afterDatasetsDraw: (chart) => {
        let indexes = []
        if (this.showAllValues) {
          indexes = chart.data.datasets.map((_d, i) => i)
        } else if (this.focusedLabel != null) {
          // po labelu, nie indeksie - eksport SVG ma inny zestaw indeksów (pomija ukryte serie)
          chart.data.datasets.forEach((d, i) => { if (d.label === this.focusedLabel) indexes.push(i) })
        }
        // wspólna lista zajętych prostokątów - kolizje wykrywamy globalnie, także między seriami
        const placed = []
        indexes.forEach((idx) => this.drawDatasetValues(chart, idx, placed))
      },
    }
  }

  // Rysuje wartości jednej serii przy każdym punkcie/słupku, kolorem tej serii.
  // Pod tekstem chip (tło + ramka), żeby etykieta była czytelna nad innymi liniami.
  // Etykietuje tylko przy ZMIANIE wartości (valueChanged) i unika kolizji (placeLabel).
  drawDatasetValues(chart, datasetIndex, placed) {
    const meta = chart.getDatasetMeta(datasetIndex)
    if (!meta || meta.hidden) return

    const ds = chart.data.datasets[datasetIndex]
    const color = ds._color || "#111827"
    const ctx = chart.ctx
    ctx.save()
    ctx.font = LABEL.font
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.lineWidth = 1
    meta.data.forEach((element, i) => {
      const raw = ds.data[chart.data.labels[i]]
      if (raw == null) return

      const value = Number(raw) // wartości z BigDecimal przychodzą jako stringi ("3.0")
      if (Number.isNaN(value)) return

      // etykietujemy tylko przy ZMIANIE wartości w serii - płaski ciąg (np. 2,2,2,2) dostaje jedną
      // etykietę na początku odcinka, zamiast powtórzonych kolidujących liczb
      const prevRaw = i > 0 ? ds.data[chart.data.labels[i - 1]] : null
      if (!this.valueChanged(prevRaw, raw)) return

      const text = this.formatValue(value)
      const bw = ctx.measureText(text).width + LABEL.padX * 2
      const bh = LABEL.h + LABEL.padY * 2
      // wartość ujemna pod punktem, dodatnia nad - żeby etykieta nie wchodziła w słupek/oś
      const baseY = value < 0 ? element.y + LABEL.offsetBelow : element.y - LABEL.offsetAbove

      const cy = this.placeLabel(element.x, baseY, bw, bh, value, placed)
      if (cy == null) return // zbyt ciasno - pomijamy etykietę (odgracanie)

      // tło + ramka (chip pod liczbą)
      ctx.beginPath()
      ctx.roundRect(element.x - bw / 2, cy - bh / 2, bw, bh, 3)
      ctx.fillStyle = "rgba(255, 255, 255, 0.85)"
      ctx.fill()
      ctx.strokeStyle = color
      ctx.stroke()

      // liczba na wierzchu
      ctx.fillStyle = color
      ctx.fillText(text, element.x, cy)
    })
    ctx.restore()
  }

  // Czy punkt wart jest etykiety przy "pokaż wartości". 0 pomijamy - brak aktywności w punkcie to szum,
  // nie wartość. Poza tym etykietujemy tylko przy ZMIANIE: brak poprzedniej (początek serii lub przerwa)
  // = traktujemy jak zmianę, więc początek odcinka dostaje etykietę.
  valueChanged(prevRaw, raw) {
    if (Number(raw) === 0) return false
    if (prevRaw == null) return true

    return Number(prevRaw) !== Number(raw)
  }

  // Szuka pozycji etykiety bez kolizji z już narysowanymi (placed). Pozycja bazowa gdy wolna; przy
  // kolizji MNIEJSZA wartość schodzi niżej, większa idzie wyżej. Zwraca y środka albo null (pomijamy).
  placeLabel(cx, baseY, bw, bh, value, placed) {
    const rectAt = (cy) => ({ x: cx - bw / 2, y: cy - bh / 2, w: bw, h: bh })
    const hitOf = (rect) => placed.find((p) => this.rectsOverlap(p, rect))

    const base = rectAt(baseY)
    const hit = hitOf(base)
    if (!hit) {
      placed.push({ ...base, value })
      return baseY
    }

    // kolizja: mniejsza od trafionej w dół (dir=1), większa/równa w górę (dir=-1)
    const dir = value < hit.value ? 1 : -1
    const step = bh + 1
    for (let k = 1; k <= 4; k++) {
      const cy = baseY + dir * step * k
      const rect = rectAt(cy)
      if (!hitOf(rect)) {
        placed.push({ ...rect, value })
        return cy
      }
    }
    return null
  }

  // Czy dwa prostokąty {x,y,w,h} nachodzą na siebie (AABB).
  rectsOverlap(a, b) {
    return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  }

  // Formatuje wartość etykiety: całkowite bez miejsc, ułamkowe (np. średnia) do 1 miejsca.
  // Akceptuje też stringi (BigDecimal z backendu serializuje się do JSON jako "3.0").
  formatValue(value) {
    const n = Number(value)
    if (Number.isNaN(n)) return ""

    return Number.isInteger(n) ? String(n) : n.toFixed(1)
  }

  // hex "#rrggbb" -> "rgba(r,g,b,alpha)" - przygasza zachowując odcień serii
  fade(hex, alpha) {
    const n = parseInt(hex.slice(1), 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
  }
}
